import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { queueApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Loader2, LayoutList, Inbox, RefreshCw, Bell, LayoutDashboard, Plus, PackagePlus } from "lucide-react";
import { ShelfCard } from "../components/shelf/ShelfCard";
import { MyWorkCard } from "../components/shelf/MyWorkCard";
import { ReleaseDialog } from "../components/shelf/ReleaseDialog";
import { AdditionRow } from "../components/shelf/AdditionRow";
import { ShelfDetailDialog } from "../components/shelf/ShelfDetailDialog";
import { ReturnFormDialog } from "../components/shelf/ReturnFormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function DigitalShelf() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [shelf, setShelf] = useState({ items: [], hidden_count: 0, wip_held: 0, wip_limit: 3, can_claim: true });
  const [mine, setMine] = useState({ items: [], wip_held: 0, wip_limit: 3 });
  const [notifications, setNotifications] = useState([]);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [additions, setAdditions] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [tab, setTab] = useState("shelf");

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const [s, m, n, a] = await Promise.all([
        queueApi.getShelf(),
        queueApi.getMine(),
        queueApi.getNotifications(),
        queueApi.getMyAdditions(),
      ]);
      setShelf(s.data);
      setMine(m.data);
      setNotifications(n.data);
      setAdditions(a.data);
    } catch (e) {
      toast.error("Couldn't load the shelf");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  const claim = async (item) => {
    setBusy(true);
    try {
      await queueApi.claim(item.id);
      setDetailItem(null);
      toast.success(`${item.client_name} is yours.`);
      setTab("mine");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't claim that");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (item, status) => {
    setBusy(true);
    try {
      await queueApi.setStatus(item.id, { status });
      toast.success(
        status === "complete"
          ? `${item.client_name} finished — whoever put it on the shelf has been told.`
          : "Status updated."
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update status");
    } finally {
      setBusy(false);
    }
  };

  const release = async (payload) => {
    setBusy(true);
    try {
      await queueApi.release(releaseTarget.id, payload);
      toast.success(`${releaseTarget.client_name} is back on the shelf.`);
      setReleaseTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't release that");
    } finally {
      setBusy(false);
    }
  };

  const dismissNotifications = async () => {
    await queueApi.markNotificationsSeen();
    setNotifications([]);
  };

  const submitForm = async (payload) => {
    setBusy(true);
    try {
      if (editing) {
        await queueApi.updateItem(editing.id, payload);
        toast.success("Work updated.");
      } else {
        await queueApi.createItem(payload);
        toast.success("It's on the shelf — anyone with time can pick it up.");
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that");
    } finally {
      setBusy(false);
    }
  };

  const removeAddition = async () => {
    setBusy(true);
    try {
      await queueApi.deleteItem(deleteTarget.id);
      toast.success("Removed.");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete that");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="shelf-loading">
        <Loader2 className="animate-spin text-muted-foreground" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-7 animate-fade-in" data-testid="digital-shelf-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight">Digital Shelf</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Open work, please claim if you need work. You're holding{" "}
            <span className="font-semibold text-foreground tabular-nums">
              {mine.wip_held} of {mine.wip_limit}
            </span>
            .
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            data-testid="shelf-add-return-btn"
          >
            <Plus size={14} /> Put Work on Shelf
          </Button>
          <Button variant="outline" size="sm" onClick={() => load()} className="gap-2" data-testid="shelf-refresh-btn">
            <RefreshCw size={14} /> Refresh
          </Button>
          {isAdmin && (
            <Button asChild variant="outline" size="sm" className="gap-2" data-testid="shelf-board-link">
              <Link to="/shelf/board">
                <LayoutDashboard size={14} /> Board
              </Link>
            </Button>
          )}
        </div>
      </div>

      {notifications.length > 0 && (
        <div
          className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 p-4"
          data-testid="shelf-notifications"
        >
          <Bell size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 space-y-1">
            {notifications.slice(0, 4).map((n) => (
              <p key={n.id} className="text-sm text-foreground" data-testid={`shelf-notification-${n.id}`}>
                {n.message}
              </p>
            ))}
            {notifications.length > 4 && (
              <p className="text-xs text-muted-foreground pt-0.5" data-testid="shelf-notification-overflow">
                and {notifications.length - 4} more
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={dismissNotifications} data-testid="dismiss-notifications-btn">
            Dismiss
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="shelf-tabs">
          <TabsTrigger value="shelf" className="gap-2" data-testid="tab-shelf">
            <LayoutList size={14} /> Shelf ({shelf.items.length})
          </TabsTrigger>
          <TabsTrigger value="mine" className="gap-2" data-testid="tab-my-work">
            <Inbox size={14} /> My Work ({mine.items.length})
          </TabsTrigger>
          <TabsTrigger value="additions" className="gap-2" data-testid="tab-my-additions">
            <PackagePlus size={14} /> I Added ({additions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="shelf" className="mt-6 space-y-4">
          {!shelf.can_claim && (
            <p
              className="text-sm rounded-lg border border-border bg-muted/50 px-4 py-3"
              data-testid="wip-limit-warning"
            >
              You're at your limit of {shelf.wip_limit}. Finish or release something first.
            </p>
          )}
          {shelf.items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center" data-testid="shelf-empty">
              Nothing on the shelf right now.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="shelf-grid">
              {shelf.items.map((item) => (
                <ShelfCard
                  key={item.id}
                  item={item}
                  canClaim={shelf.can_claim}
                  claiming={busy}
                  onClaim={claim}
                  onOpen={setDetailItem}
                />
              ))}
            </div>
          )}
          {shelf.hidden_count > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="shelf-hidden-count">
              {shelf.hidden_count} more waiting behind these.
            </p>
          )}
        </TabsContent>

        <TabsContent value="mine" className="mt-6">
          {mine.items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center" data-testid="my-work-empty">
              You're not holding anything. Grab something off the shelf.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-work-grid">
              {mine.items.map((item) => (
                <MyWorkCard
                  key={item.id}
                  item={item}
                  busy={busy}
                  onStatus={changeStatus}
                  onRelease={setReleaseTarget}
                  onOpen={setDetailItem}
                />
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            It stays yours until you finish it. Buried? Hand it back and someone with room can pick it up.
          </p>
        </TabsContent>

        <TabsContent value="additions" className="mt-6">
          {additions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center" data-testid="additions-empty">
              You haven't put anything on the shelf yet.
            </p>
          ) : (
            <div className="space-y-3" data-testid="additions-list">
              {additions.map((item) => (
                <AdditionRow
                  key={item.id}
                  item={item}
                  busy={busy}
                  onEdit={(i) => {
                    setEditing(i);
                    setFormOpen(true);
                  }}
                  onDelete={setDeleteTarget}
                />
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            You can edit or remove what you added until someone claims it.
          </p>
        </TabsContent>
      </Tabs>

      <ReleaseDialog
        item={releaseTarget}
        open={!!releaseTarget}
        onOpenChange={(o) => !o && setReleaseTarget(null)}
        onConfirm={release}
        busy={busy}
      />
      <ShelfDetailDialog
        item={detailItem}
        open={!!detailItem}
        onOpenChange={(o) => !o && setDetailItem(null)}
        onClaim={claim}
        canClaim={shelf.can_claim}
        busy={busy}
      />
      <ReturnFormDialog
        currentUserName={user?.name}
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          if (!o) setEditing(null);
        }}
        onSubmit={submitForm}
        busy={busy}
        editing={editing}
      />
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="addition-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.client_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This takes the return off the queue entirely.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="addition-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={removeAddition}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="addition-delete-confirm-btn"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
